import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate, Link } from "react-router-dom";
import { Calendar, ArrowRight, Filter, X } from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import axios from "axios";
import Header from "../components/Header";
import Footer from "../components/Footer";
import WhatsAppWidget from "../components/WhatsAppWidget";
import SEO from "../components/SEO";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GalleryPage = () => {
  const [events, setEvents] = useState([]);
  const [years, setYears] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    fetchFilters();
    fetchEvents();
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [selectedYear, selectedType]);

  const fetchFilters = async () => {
    try {
      const [yearsRes, typesRes] = await Promise.all([
        axios.get(`${API}/events/years`),
        axios.get(`${API}/events/types`),
      ]);
      setYears(yearsRes.data.years || []);
      setEventTypes(typesRes.data.types || []);
    } catch (error) {
      console.error("Error fetching filters:", error);
    }
  };

  const fetchEvents = async () => {
    setLoading(true);
    try {
      let url = `${API}/events`;
      const params = new URLSearchParams();
      
      // Only add parameters if they have actual filter values (not "all" or empty)
      if (selectedYear && selectedYear !== "all") {
        params.append("year", selectedYear);
      }
      if (selectedType && selectedType !== "all") {
        params.append("event_type", selectedType);
      }
      
      if (params.toString()) url += `?${params.toString()}`;

      const response = await axios.get(url);
      setEvents(response.data);
    } catch (error) {
      console.error("Error fetching events:", error);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setSelectedYear("");
    setSelectedType("");
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-ZA", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Gallery | Events & Activities"
        description="Explore Mheshe Attorneys' journey through conferences, workshops, community initiatives, and milestone celebrations. View our events and activities gallery."
        url="https://mhesheattorneys.co.za/gallery"
      />
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-charcoal">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <p className="uppercase tracking-[0.2em] text-xs font-bold text-brand-gold mb-4">
              Our Events & Activities
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold text-white leading-tight tracking-tighter mb-6">
              Gallery
            </h1>
            <p className="text-white/60 font-sans text-lg max-w-2xl mx-auto">
              Explore our journey through conferences, workshops, community
              initiatives, and milestone celebrations.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-8 bg-light-grey border-b border-charcoal/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-charcoal/60">
              <Filter className="h-5 w-5" />
              <span className="font-sans text-sm">Filter by:</span>
            </div>

            <div className="flex flex-wrap items-center gap-4">
              <Select value={selectedYear} onValueChange={(value) => setSelectedYear(value === "all" ? "" : value)}>
                <SelectTrigger className="w-[140px] bg-white border-charcoal/20 rounded-none">
                  <SelectValue placeholder="All Years" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="all">All Years</SelectItem>
                  {years.map((year) => (
                    <SelectItem key={year} value={year.toString()}>
                      {year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={selectedType} onValueChange={(value) => setSelectedType(value === "all" ? "" : value)}>
                <SelectTrigger className="w-[160px] bg-white border-charcoal/20 rounded-none">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="all">All Types</SelectItem>
                  {eventTypes.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {(selectedYear || selectedType) && (
                <Button
                  variant="ghost"
                  onClick={clearFilters}
                  className="text-charcoal/60 hover:text-charcoal"
                >
                  <X className="h-4 w-4 mr-1" />
                  Clear
                </Button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Events Grid */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-gold mx-auto"></div>
              <p className="mt-4 text-charcoal/60">Loading events...</p>
            </div>
          ) : events.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-charcoal/60 font-sans text-lg">
                No events found. Check back soon for updates!
              </p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {events.map((event, index) => (
                <motion.article
                  key={event.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="group cursor-pointer"
                  onClick={() => navigate(`/gallery/${event.id}`)}
                  data-testid={`event-card-${index}`}
                >
                  {/* Image */}
                  <div className="relative aspect-[4/3] overflow-hidden mb-6">
                    <img
                      src={event.cover_image}
                      alt={event.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="bg-brand-gold text-charcoal px-3 py-1 text-xs font-semibold uppercase tracking-wider">
                        {event.event_type}
                      </span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex items-center gap-2 text-charcoal/50 mb-3">
                    <Calendar className="h-4 w-4" />
                    <span className="font-sans text-sm">
                      {formatDate(event.event_date)}{event.event_end_date ? ` — ${formatDate(event.event_end_date)}` : ''}
                    </span>
                  </div>

                  <h2 className="font-serif text-2xl text-charcoal mb-3 group-hover:text-brand-gold transition-colors">
                    {event.title}
                  </h2>

                  <p className="text-charcoal/60 font-sans text-sm leading-relaxed mb-4 line-clamp-3">
                    {event.short_description}
                  </p>

                  <div className="flex items-center text-brand-gold font-sans text-sm font-medium uppercase tracking-wider">
                    <span>View More</span>
                    <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-2 transition-transform" />
                  </div>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-light-grey">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-3xl text-charcoal mb-4">
            Want to Work With Us?
          </h2>
          <p className="text-charcoal/60 font-sans mb-8 max-w-xl mx-auto">
            Get in touch to discuss how Mheshe Attorneys can assist with your
            legal needs.
          </p>
          <Link to="/#lead-capture">
            <Button className="btn-gold px-8 py-4 text-base font-semibold uppercase tracking-wider rounded-none">
              Book a Consultation
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
      <WhatsAppWidget />
    </div>
  );
};

export default GalleryPage;
